import { all, put, select, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import equal from 'fast-deep-equal';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  fetchRequirementTemplate,
  fetchRequirementTemplateDelete,
  fetchRequirementTemplateSetDefault,
  fetchRequirementTemplateUpdate,
} from 'APIPackage/AccountModule/services/requirementTemplatesApi';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { IPageRequirementsTemplate } from 'APIPackage/AccountModule/interfaces/IPageRequirementsTemplate';
import { IRequirementsTemplate } from 'APIPackage/AccountModule/interfaces/IRequirementsTemplate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { addNoticeAction, removeAllNoticesAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { IRequirementsTemplateUpdate } from 'APIPackage/AccountModule/interfaces/IRequirementsTemplateUpdate';
import {
  REQUIREMENTS_TEMPLATE_CHANGES_WARNING,
  REQUIREMENTS_TEMPLATE_DEFAULT_IMPOSSIBLE_WARNING,
  REQUIREMENTS_TEMPLATE_DEFAULT_WARNING,
  REQUIREMENTS_TEMPLATE_DELETE_SUCCESSFULLY,
  REQUIREMENTS_TEMPLATE_UPDATE_ERROR_DUPLICATE,
  REQUIREMENTS_TEMPLATE_UPDATE_SUCCESSFULLY,
} from 'CorePackage/MainModule/constants/notices';
import { REQUIREMENTS_TEMPLATE_ERROR_DUPLICATE_MESSAGE_PART } from 'CorePackage/MainModule/constants/httpErrors';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { accountRequirementTemplatesGet } from 'CorePackage/AccountModule/actions/accountActions';
import { getAccountOrganizationsData } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import { UserRequirementsActionsEnum } from '../enums/UserRequirementsActionsEnum';
import {
  getUserRequirementsActiveTemplateId,
  getUserRequirementsOrder,
  getUserRequirementsPage,
  getUserRequirementsPageCount,
  getUserRequirementsTemplateList,
  getUserRequirementsTemplates,
  getUserRequirementsTotalCount,
} from '../selectors/userRequirementsSelectors';
import {
  userRequirementsSetTemplateList,
  userRequirementsSetActiveTemplateId,
  userRequirementsUpdate,
  userRequirementsDelete,
  userRequirementsChangeDefault,
  userRequirementsFormSubmit,
} from '../actions/userRequirementsAction';
import { USER_REQUIREMENTS_PAGE_SIZE } from '../constants/useRequirementsPageSize';
import { initialRequirementTemplatesParams } from '../constants/initialRequirementTemplatesParams';

const sortList = (list: Array<IRequirementsTemplate>) => {
  let newTemplatesList: Array<IRequirementsTemplate> = [];

  if (list.length) {
    newTemplatesList = list.map(template => {
      return {
        ...template,
        requirements: [...(template.requirements || [])]
          ?.map(requirement => {
            return {
              ...requirement,
              date: requirement.date && formatDateLocale({ date: requirement.date, dateFormat: DATE_FORMAT_LONG_FNS }),
            };
          })
          .sort((a, b) => {
            const indexA = a.index;
            const indexB = b.index;

            if (typeof indexA === 'number' && typeof indexB === 'number') {
              return indexA - indexB;
            }
            if (!indexA) {
              return 1;
            }
            if (!indexB) {
              return -1;
            }

            return 0;
          }),
      };
    });
    newTemplatesList = newTemplatesList.sort((a, b) => {
      const idA = a.id;
      const idB = b.id;

      if (typeof idA === 'number' && typeof idB === 'number') {
        return idB - idA;
      }
      if (!idA) {
        return -1;
      }
      if (!idB) {
        return 1;
      }

      return 0;
    });
  }

  return newTemplatesList;
};

const commonCatalogsNames: Array<CommonCatalogsNamesEnum> = [CommonCatalogsNamesEnum.USER_KNOS];

export function* watchUserRequirementsSaga() {
  yield takeEvery(UserRequirementsActionsEnum.USER_REQUIREMENTS_INIT_PAGE, userRequirementsInitPageSaga);
  yield takeEvery(UserRequirementsActionsEnum.USER_REQUIREMENTS_GET_TEMPLATE_LIST, userRequirementsGetTemplateListSaga);
  yield takeEvery(
    UserRequirementsActionsEnum.USER_REQUIREMENTS_GET_TEMPLATE_LIST_NEXT_PAGE,
    userRequirementsGetTemplateListNextPageSaga,
  );
  yield takeEvery(
    UserRequirementsActionsEnum.USER_REQUIREMENTS_SET_ACTIVE_TEMPLATE_ID,
    userRequirementsSetActiveTemplateIdSaga,
  );
  yield takeEvery(UserRequirementsActionsEnum.USER_REQUIREMENTS_UPDATE, userRequirementsUpdateSaga);
  yield takeEvery(UserRequirementsActionsEnum.USER_REQUIREMENTS_DELETE, userRequirementsDeleteSaga);
  yield takeEvery(UserRequirementsActionsEnum.USER_REQUIREMENTS_CHANGE_DEFAULT, userRequirementsChangeDefaultSaga);
  yield takeEvery(UserRequirementsActionsEnum.USER_REQUIREMENTS_FORM_SUBMIT, userRequirementsFormSubmitSaga);
}

function* userRequirementsInitPageSaga() {
  yield all([...commonCatalogsNames.map(catalogName => put(catalogGet(catalogName)))]);
  yield put(accountRequirementTemplatesGet(initialRequirementTemplatesParams));
}

function* userRequirementsGetTemplateListSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const templates = getUserRequirementsTemplates(state);
    const templateList = getUserRequirementsTemplateList(state);

    if (templates?.data) {
      const res: IPageRequirementsTemplate = templates?.data;
      const filteredList = sortList([
        ...templateList,
        ...res.list.filter(newItem => !templateList.find(item => item.id === newItem.id)),
      ]);

      yield put(userRequirementsSetTemplateList(filteredList, res.page, res.pageCount, res.totalCount));
      yield put(userRequirementsSetActiveTemplateId(filteredList[0]?.id));
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: error.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.GET_TEMPLATES,
        }),
      );
    },
  });
}

function* userRequirementsGetTemplateListNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const order = getUserRequirementsOrder(state);
    const page = getUserRequirementsPage(state);
    const totalCount = getUserRequirementsTotalCount(state);
    const pageCount = getUserRequirementsPageCount(state);
    const templateList = getUserRequirementsTemplateList(state);

    yield put(
      accountRequirementTemplatesGet({
        isCaching: false,
        order,
        page: (page + 1).toString(),
        size: USER_REQUIREMENTS_PAGE_SIZE,
      }),
    );
    yield put(userRequirementsSetTemplateList(templateList, page + 1, pageCount, totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* userRequirementsSetActiveTemplateIdSaga(action: ActionType<typeof userRequirementsSetActiveTemplateId>) {
  function* funcTry() {
    const { id } = action.payload;

    if (typeof id !== 'number') {
      return;
    }
    const state: IRootState = yield select();
    const templatesList = getUserRequirementsTemplateList(state);
    const page = getUserRequirementsPage(state);
    const pageCount = getUserRequirementsPageCount(state);
    const totalCount = getUserRequirementsTotalCount(state);
    const requirementTemplate: IRequirementsTemplate = yield fetchRequirementTemplate(id);

    if (requirementTemplate) {
      yield put(
        userRequirementsSetTemplateList(
          templatesList.map(item => {
            if (item.id === requirementTemplate.id) {
              return {
                ...requirementTemplate,
                byDefault: item.byDefault,
                requirements:
                  requirementTemplate.requirements &&
                  requirementTemplate.requirements.map(value => ({
                    ...value,
                    date: formatDate({
                      date: value.date as string,
                      dateFormat: DATE_FORMAT_LONG_FNS,
                    }),
                  })),
              };
            }

            return item;
          }),
          page,
          pageCount,
          totalCount,
        ),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* userRequirementsUpdateSaga(action: ActionType<typeof userRequirementsUpdate>) {
  function* funcTry() {
    const { values } = action.payload;
    const { templateName: name, organisation, supervisionType, requirements } = values;
    const state: IRootState = yield select();
    const templatesList = getUserRequirementsTemplateList(state);
    const activeTemplateId = getUserRequirementsActiveTemplateId(state);
    const activeTemplate = templatesList.find(item => item.id === activeTemplateId);
    const page = getUserRequirementsPage(state);
    const pageCount = getUserRequirementsPageCount(state);
    const totalCount = getUserRequirementsTotalCount(state);
    const knoOrganizationId = organisation?.id;
    const supervisionTypeId = supervisionType?.id;

    if (knoOrganizationId && supervisionTypeId) {
      const data: IRequirementsTemplateUpdate = {
        id: activeTemplate?.id || undefined,
        name,
        byDefault: activeTemplate?.byDefault || false,
        knoOrganizationId,
        supervisionType: supervisionTypeId,
        requirements: requirements?.map((item, index) => ({
          ...item,
          date: item.date
            ? formatDate({
                date: item.date,
                dateFormat: SYSTEM_DATE_FORMAT_FNS,
              })
            : '',
          index,
        })),
      };
      const response: IRequirementsTemplate = yield fetchRequirementTemplateUpdate({ data });
      const newTemplatesList = () => {
        let prevItems = templatesList;
        const isItemExist = prevItems.some(item => item.id === response.id);

        if (response.byDefault) {
          prevItems = prevItems.map(item => {
            return {
              ...item,
              byDefault: false,
            };
          });
        }
        if (!isItemExist) {
          return [...prevItems, response];
        }

        return prevItems.map(item => {
          if (item.id === response.id) {
            return response;
          }

          return item;
        });
      };

      yield put(userRequirementsSetTemplateList(newTemplatesList(), page, pageCount, totalCount));
      yield put(userRequirementsSetActiveTemplateId(response.id));
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_TEMPLATE_UPDATE_SUCCESSFULLY,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.UPDATE_TEMPLATE,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      const { message, text } = error;

      yield put(
        addNoticeAction({
          renderContent:
            message && message.indexOf(REQUIREMENTS_TEMPLATE_ERROR_DUPLICATE_MESSAGE_PART) >= 0
              ? REQUIREMENTS_TEMPLATE_UPDATE_ERROR_DUPLICATE
              : text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.UPDATE_TEMPLATE,
        }),
      );
    },
  });
}

function* userRequirementsDeleteSaga(action: ActionType<typeof userRequirementsDelete>) {
  function* funcTry() {
    const { id } = action.payload;
    const state: IRootState = yield select();
    const templatesList = getUserRequirementsTemplateList(state);
    const page = getUserRequirementsPage(state);
    const pageCount = getUserRequirementsPageCount(state);
    const totalCount = getUserRequirementsTotalCount(state);
    const activeTemplateId = getUserRequirementsActiveTemplateId(state);
    const response: IRequirementsTemplate = yield fetchRequirementTemplateDelete(id);

    yield put(
      userRequirementsSetTemplateList(
        templatesList.filter(item => item.id !== response.id),
        page,
        pageCount,
        totalCount,
      ),
    );
    if (activeTemplateId === id) {
      yield put(userRequirementsSetActiveTemplateId(templatesList.find(item => item.id !== id)?.id || 'new'));
    }
    yield put(
      addNoticeAction({
        renderContent: REQUIREMENTS_TEMPLATE_DELETE_SUCCESSFULLY,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.DELETE_TEMPLATE,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: error.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DELETE_TEMPLATE,
        }),
      );
    },
  });
}

function* userRequirementsChangeDefaultSaga(action: ActionType<typeof userRequirementsChangeDefault>) {
  function* funcTry() {
    const { id } = action.payload;
    const state: IRootState = yield select();
    const templatesList = getUserRequirementsTemplateList(state);
    const knoOrganisations = getAccountOrganizationsData(state).kno;
    const page = getUserRequirementsPage(state);
    const pageCount = getUserRequirementsPageCount(state);
    const totalCount = getUserRequirementsTotalCount(state);

    if (id) {
      const template = templatesList.find(tId => tId.id === id);

      if (template) {
        const { knoOrganizationId } = template;
        const defaultKnoOrganisationId = knoOrganisations.find(item => item.byDefault)?.id;

        if (knoOrganizationId && knoOrganizationId === defaultKnoOrganisationId) {
          const changeDefaultTemplateResponse: IRequirementsTemplate = yield fetchRequirementTemplateSetDefault(id);

          if (changeDefaultTemplateResponse) {
            const newTemplatesList = [...templatesList];
            const oldTemplateDefaultIndex = templatesList.findIndex(item => item.byDefault);

            if (oldTemplateDefaultIndex >= 0) {
              newTemplatesList[oldTemplateDefaultIndex] = {
                ...newTemplatesList[oldTemplateDefaultIndex],
                byDefault: false,
              };
            }
            newTemplatesList[newTemplatesList.findIndex(item => item.id === template.id)] = {
              ...template,
              byDefault: true,
            };
            yield put(userRequirementsSetTemplateList(newTemplatesList, page, pageCount, totalCount));

            yield put(
              addNoticeAction({
                renderContent: `Обязательное требование "${changeDefaultTemplateResponse.name}" выбрано по умолчанию`,
                noticeType: NoticeTypesEnum.SUCCESS,
                messageType: MessageTypesEnum.SELECT_REQUIREMENT,
              }),
            );
          }
        } else {
          yield put(
            addNoticeAction({
              renderContent: REQUIREMENTS_TEMPLATE_DEFAULT_IMPOSSIBLE_WARNING,
              noticeType: NoticeTypesEnum.WARNING,
              messageType: MessageTypesEnum.DEFAULT_IMPOSSIBLE_TEMPLATE,
            }),
          );
        }
      }
    } else {
      addNoticeAction({
        renderContent: REQUIREMENTS_TEMPLATE_DEFAULT_WARNING,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.DEFAULT_TEMPLATE,
      });
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: error.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.CHANGE_DEFAULT_TEMPLATE,
        }),
      );
    },
  });
}

function* userRequirementsFormSubmitSaga(action: ActionType<typeof userRequirementsFormSubmit>) {
  const { values, initialValues, resetForm } = action.payload;

  const isFormChange = !equal(
    { ...values, questions: values.requirements.filter(item => !item.deleteId) },
    { ...initialValues, questions: initialValues.requirements },
  );

  if (isFormChange) {
    yield put(removeAllNoticesAction());
    yield put(
      userRequirementsUpdate({
        ...values,
        requirements: values.requirements
          .filter(item => !item.deleteId)
          .map((item, index) => ({
            id: item.id,
            name: item.name,
            props: item.props,
            number: item.number,
            date: formatDate({ date: item.date || '', dateFormat: DATE_FORMAT_LONG_FNS }),
            paragraph: item.paragraph,
            part: item.part,
            article: item.article,
            index,
          })),
      }),
    );
    if (resetForm) {
      resetForm();
    }
  } else {
    yield put(
      addNoticeAction({
        renderContent: REQUIREMENTS_TEMPLATE_CHANGES_WARNING,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.SAVE_TEMPLATE_CHANGES,
      }),
    );
  }
}
